import { Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { BehaviorSubject, Observable } from 'rxjs';
import { filter } from 'rxjs/operators';

import { AppCoreService } from '../../core/services/app-core.service';
import { SecurityService } from '../services/security.service';
import { Five9StylingService } from './five9-styling.service';
import { Five9AccessibilityService } from './five9-accessibility.service';
import { LayoutModeService } from './layout-mode.service';

@Injectable({
	providedIn: 'root'
})
export class Five9ChatService {
	// id of the container Five9's Chat Wrapper library creates/removes on init()/destroy()
	// (see cdn.prod.us.five9.net/static/stable/chat/wrapper/index.js)
	private static readonly WRAPPER_CONTAINER_ID = 'fn-chat-iframe-container';

	private expandedSubject = new BehaviorSubject<boolean>(false);
	public isExpanded$: Observable<boolean> = this.expandedSubject.asObservable();

	private showChatFabSubject = new BehaviorSubject<boolean>(false);
	public showChatFab$: Observable<boolean> = this.showChatFabSubject.asObservable();

	private _stateWatcherAttached = false;
	private wrapperOptions: { cdn: string; integrationId: string; chatDeploymentId: string } = null;
	private wrapperVisibleRoutes: string[] = [];

	constructor(
		private securityService: SecurityService,
		private appCoreService: AppCoreService,
		private five9StylingService: Five9StylingService,
		private five9AccessibilityService: Five9AccessibilityService,
		private layoutModeService: LayoutModeService,
		private router: Router
	) {}

	// ================= LEGACY FIVE9 SOCIAL WIDGET (e.g. MO) =================

	/**
	 * Call this once the Five9 library script has finished loading.
	 * Attaches the state watcher, adds the widget for authenticated users,
	 * and starts watching for future auth and layout changes.
	 */
	initialize(): void {
		if (!this.isWidgetLoaded()) {
			console.warn('Five9ChatService.initialize() called before library was ready');
			return;
		}

		this.attachStateWatcher();

		if (this.widget.widgetAdded) {
			console.log('Five9: widget already added (probably from previous page load)');
			this.setupChatLayoutSubscription();
			return;
		}

		if (this.securityService.isAuthenticated() && this.securityService.getCurrentUser()) {
			this.addChatData();
		}

		this.attachUserAuthWatcher();

		this.five9AccessibilityService.init();
	}

	// ================= NEWER FIVE9 CHAT WRAPPER (e.g. OH) =================
	// Different product/script than the Social Widget above (window.F9.Chat.Wrapper
	// instead of window.Five9SocialWidget). Kept separate rather than merged in.

	/**
	 * Entry point for the wrapper integration, called once its script has loaded.
	 * visibleRoutes is the client-configured list of pages to show the widget on
	 * (clientConfig.five9LiveChat.visibleRoutes) since the widget has no built-in
	 * way to restrict which pages it shows on.
	 */
	initializeWrapper(
		options: { cdn: string; integrationId: string; chatDeploymentId: string },
		visibleRoutes: string[]
	): void {
		this.wrapperOptions = options;
		this.wrapperVisibleRoutes = visibleRoutes ?? [];

		this.applyWrapperVisibility();

		// re-check visibility whenever the user logs in/out or navigates
		this.securityService.currentUserChanged.subscribe(() => this.applyWrapperVisibility());
		this.router.events
			.pipe(filter((event) => event instanceof NavigationEnd))
			.subscribe(() => this.applyWrapperVisibility());
	}

	// Only show the wrapper widget for authenticated members on client-configured routes.
	private applyWrapperVisibility(): void {
		const isAuthenticated = this.securityService.isAuthenticated() && !!this.securityService.getCurrentUser();
		const currentPath = this.router.url.split('?')[0];
		const onVisibleRoute = this.isPathInVisibleRoutes(currentPath);
		this.setWrapperVisible(isAuthenticated && onVisibleRoute);
	}

	// Supports exact routes (e.g. "/menu/home") and wildcard prefixes (e.g. "/menu/*",
	// which matches "/menu/home" and everything under it) so clients don't have to list
	// every nested route individually.
	private isPathInVisibleRoutes(currentPath: string): boolean {
		return this.wrapperVisibleRoutes.some((route) => {
			if (route.endsWith('/*')) {
				const prefix = route.slice(0, -2);
				return currentPath === prefix || currentPath.startsWith(`${prefix}/`);
			}
			return currentPath === route;
		});
	}

	// Mounts/tears down the wrapper widget to match the desired visibility.
	private setWrapperVisible(visible: boolean): void {
		const isMounted = !!document.getElementById(Five9ChatService.WRAPPER_CONTAINER_ID);
		if (visible && !isMounted && this.wrapperOptions) {
			this.initFive9ChatWrapper(this.wrapperOptions);
		} else if (!visible && isMounted) {
			this.destroyFive9ChatWrapper();
		}
	}

	// Creates the wrapper widget's DOM/iframe via F9.Chat.Wrapper.init().
	private initFive9ChatWrapper(options: { cdn: string; integrationId: string; chatDeploymentId: string }): void {
		const f9 = (window as any).F9;
		if (!f9?.Chat?.Wrapper?.init) {
			console.warn('Five9ChatService: F9.Chat.Wrapper is not available');
			return;
		}
		f9.Chat.Wrapper.init(options);
	}

	// Removes the wrapper widget's DOM/iframe via F9.Chat.Wrapper.destroy().
	private destroyFive9ChatWrapper(): void {
		const f9 = (window as any).F9;
		if (f9?.Chat?.Wrapper?.destroy) {
			f9.Chat.Wrapper.destroy();
		}
	}

	/**
	 * Returns true if the chat widget is currently EXPANDED
	 * (pre-chat form OR live conversation).
	 * This is the method you should use in your mobile/zoom detection code.
	 */
	isChatExpanded(): boolean {
		if (!this.isWidgetLoaded()) return false;
		return this.widget.data.state === 'maximized';
	}

	/**
	 * Returns true if the chat is open in ANY form (expanded or popout window)
	 */
	isChatOpen(): boolean {
		if (!this.isWidgetLoaded()) return false;
		const state = this.widget.data.state;
		return state === 'maximized' || state === 'popout';
	}

	/**
	 * Returns true only when the user is in a live conversation
	 * (past the pre-chat form).
	 */
	isLiveChatActive(): boolean {
		if (!this.isWidgetLoaded() || !this.modules?.ChatModel) return false;

		const chatModel = this.modules.ChatModel;
		const persisted = chatModel.loadData?.() || chatModel.data;
		const step = persisted?.step || chatModel.getStep?.();

		return step === 'Conversation';
	}

	maximizeChat(): void {
		if (!this.isWidgetLoaded()) return;
		this.widget.maximizeChat(this.appCoreService.clientConfig.five9LiveChat.options);
	}

	/**
	 * Shows or hides the Five9 chat button and frame elements in the DOM.
	 * Used to toggle between the desktop widget and mobile FAB.
	 */
	setWidgetVisibility(visible: boolean): void {
		const action = visible ? 'remove' : 'add';
		for (const el of Array.from(document.getElementsByClassName('five9-chat-button'))) {
			el.classList[action]('hide-chat-button');
		}
		for (const el of Array.from(document.getElementsByClassName('five9-frame'))) {
			el.classList[action]('hide-chat-button');
		}
	}

	minimizeWidget(): void {
		if (this.isWidgetLoaded() && typeof this.widget.clickMinimize === 'function') {
			this.widget.clickMinimize();
		}
	}

	removeWidget(): void {
		if (this.isWidgetLoaded() && typeof this.widget.removeWidget === 'function') {
			this.widget.removeWidget();
		}
	}

	// ====================== INTERNAL HELPERS ======================

	private get widget() {
		return (window as any).Five9SocialWidget;
	}

	private get modules() {
		return (window as any).Five9Modules;
	}

	private isWidgetLoaded(): boolean {
		return !!(this.widget && this.widget.data);
	}

	/**
	 * Attaches a listener to the library's internal setState so we know instantly
	 * when the widget expands, minimizes, or is popped out.
	 */
	private attachStateWatcher(): void {
		if (this._stateWatcherAttached || !this.widget) return;

		const originalSetState = this.widget.setState;

		this.widget.setState = (state: string) => {
			originalSetState.call(this.widget, state);

			const isExpanded = state === 'maximized';
			this.expandedSubject.next(isExpanded);

			console.log(`Five9ChatService: widget state changed → ${state}`);
		};

		this._stateWatcherAttached = true;

		// Set initial value immediately
		this.expandedSubject.next(this.isChatExpanded());

		console.log('Five9ChatService: state watcher attached');
	}

	private addChatData(): void {
		this.appCoreService.clientConfig.five9LiveChat.options.fields.PortalAccountId.value =
			this.securityService.getCurrentUser().userId;
		this.widget.addWidget(this.appCoreService.clientConfig.five9LiveChat.options);
		this.setupChatLayoutSubscription();
		this.five9StylingService.enableNarrowMinimizedButton();
	}

	/**
	 * Subscribes to auth changes and adds or removes the widget as the user
	 * logs in or out. Called once after the library script loads.
	 * No unsubscribe needed — this service is providedIn: 'root'.
	 */
	private attachUserAuthWatcher(): void {
		this.securityService.currentUserChanged.subscribe(() => {
			if (this.securityService.isAuthenticated() && !this.widget.widgetAdded) {
				this.addChatData();
			} else if (this.widget.widgetAdded) {
				this.widget.removeWidget();
				this.five9StylingService.disableNarrowMinimizedButton();
				this.showChatFabSubject.next(false);
			}
		});
	}

	/**
	 * Reactively switches between the desktop widget and the mobile FAB when
	 * the viewport crosses 992px. Handles browser zoom (Section 508) as well
	 * as window resize. Called each time a widget is added.
	 */
	private setupChatLayoutSubscription(): void {
		this.layoutModeService.isDesktopLayout$.subscribe((isDesktop) => {
			if (!this.widget.widgetAdded) return;

			if (isDesktop && !this.isChatExpanded()) {
				this.showChatFabSubject.next(false);
				this.setWidgetVisibility(true);
				this.five9StylingService.enableNarrowMinimizedButton();
			} else if (!this.isChatExpanded()) {
				this.showChatFabSubject.next(true);
				this.setWidgetVisibility(false);
				this.five9StylingService.disableNarrowMinimizedButton();
			}
		});
	}
}
